import { Injectable } from '@angular/core';

import * as Models from '../../../models/index';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { environment } from '../../../../../environments/environment';
import {Router} from '@angular/router';
import {BusinessService} from '../business.service';
import {Action} from '../../../models/common.enums';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userManager: PublicClientApplication;

  private tokenRequest: any;
  private silentProfileRequest: SilentRequest;
  private profileRedirectRequest: RedirectRequest;

  private defaultAlias: string;
  private account: AccountInfo;
  private pageReloadNeeded: boolean;

  constructor() {
    const envClientId = environment.clientId;
    const envTenantId = environment.tenant;

    this.tokenRequest = {
      scopes: [`${envClientId}/user_impersonation`],
    };

    this.silentProfileRequest = {
      scopes: [`${envClientId}/user_impersonation`],
      account: null,
      forceRefresh: false,
    };

    this.profileRedirectRequest = {
      scopes: [`${envClientId}/user_impersonation`],
      redirectStartPage: window.location.href,
    };

    this.userManager = new PublicClientApplication({
      auth: {
        clientId: envClientId,
        authority: `https://login.microsoftonline.com/${envTenantId}/`,
        // navigateToLoginRequestUrl: true
        redirectUri: environment.baseUrl
        //redirectUri: 'http://localhost:4444'
      },
      cache: {
        cacheLocation: 'localStorage',
      },
      system: {
        // loggerOptions: {
        //   loggerCallback: (level, message, containsPii) => {
        //     switch (level) {
        //       case LogLevel.Error:
        //         console.error(message);
        //         return;
        //       case LogLevel.Info:
        //         console.log(message);
        //         return;
        //       case LogLevel.Verbose:
        //         console.log(message);
        //         return;
        //       case LogLevel.Warning:
        //         console.warn(message);
        //         return;
        //     }
        //   },
        // },
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0,
      },
    });

    this.defaultAlias = '';
    const those = this;

    this.userManager
      .handleRedirectPromise()
      .then((response) => {
        this.account = response ? response.account : those.getAccount();
        // console.log(response);
        if (response && (response.accessToken || response.idToken)) {
          localStorage.setItem(Models.CE.StorageKeys.accessToken, response.accessToken); // response.idToken || 
          localStorage.setItem(Models.CE.StorageKeys.accessTokenExpiration, response.expiresOn.toString());

          if (response.accessToken) {
            if (this.pageReloadNeeded) {
              window.location.reload();
            }
          }
        }
      })
      .catch(console.error);
  }

  private getAccount(): any {
    const currentAccounts = this.userManager.getAllAccounts();
    if (currentAccounts === null) {
      // console.log('No accounts detected');
      return null;
    }

    if (currentAccounts.length > 1) {
      // console.warn('Multiple accounts detected, need to add choose account code.');
      return currentAccounts[0];
    } else if (currentAccounts.length === 1) {
      return currentAccounts[0];
    }
  }

  async login(): Promise<void> {
    try {
      await this.userManager.loginRedirect(this.tokenRequest);
    } catch (e) {
      if (e instanceof BrowserAuthError && e.errorCode === 'interaction_in_progress') {
        // console.log('swallowed BrowserAuthError(interaction_in_progress)');
      } else {
        throw e;
      }
    }
  }

  async authorizeUser(): Promise<boolean> {
    const account = this.account || this.getAccount();
    //console.log(account);
    if (account) {
      //console.log("Auth");
      localStorage.setItem('email', account.username);
      localStorage.setItem('name',account.name);
      try {
        this.silentProfileRequest.account = account;
        const response: AuthenticationResult = await this.userManager.acquireTokenSilent(this.silentProfileRequest);
        //console.log(response);
        this.setTokenData(response);
        return true;
      } catch (e) {
        //console.log('silent token acquisition fails.');
        if (e instanceof InteractionRequiredAuthError) {
          //console.log('acquiring token using redirect');
          try {
            const response: any = await this.userManager.acquireTokenRedirect(this.profileRedirectRequest);
            this.setTokenData(response);

            return true;
          } catch (err) {
            // console.error(err);
            // console.log('getUser(), returning null');

            return false;
          }
        } else if (/*e instanceof ClientAuthError && */ e.errorCode === 'no_tokens_found') {
          // console.log("swallowed ClientAuthError('no_tokens_found')");
          // console.log('acquiring token using redirect');
          try {
            const response: any = await this.userManager.acquireTokenRedirect(this.profileRedirectRequest);
            this.setTokenData(response);

            return true;
          } catch (err) {
            // console.error(err);
            // console.log('getUser(), returning null');

            return false;
          }
        } else {
          // console.error(e);
          // console.log('getUser(), return null.');
        }
      }
    } else {
      // console.log('getUser(), account = null');
      this.pageReloadNeeded = true;
    }
  }

  logout(): void {
     // Set logout flag first before clearing
      localStorage.setItem(Models.CE.StorageKeys.logoutRedirect, Models.CE.Action.ShouldDoLogout);
     
     // Clear specific authentication data but preserve the logout flag
     localStorage.removeItem(Models.CE.StorageKeys.accessToken);
     localStorage.removeItem(Models.CE.StorageKeys.accessTokenExpiration);
     localStorage.removeItem(Models.CE.StorageKeys.redirectPathname);
     localStorage.removeItem('email');
     localStorage.removeItem('name');
     localStorage.removeItem('roleID');
     localStorage.removeItem('IsRequestor');
     localStorage.removeItem('IsL1');
     localStorage.removeItem('IsL2');
     localStorage.removeItem('IsApprover');
     localStorage.removeItem('IsDenied');
     sessionStorage.clear();
     
     // Redirect to home to trigger the logout process
      window.location.assign('/');
 }

 doLogout():void {
     // Clear all stored tokens and user information
     localStorage.clear();
     sessionStorage.clear();

     // Clear the account from MSAL
     const currentAccount = this.getAccount();
     if (currentAccount) {
         this.userManager.setActiveAccount(null);
  }

     // Perform MSAL logout redirect
     this.userManager.logoutRedirect({
         postLogoutRedirectUri: window.location.origin
     })
          .then(_ => {})
          .catch(_ => {});
  }

  get userInfo(): object | null {
    return this.userManager.getActiveAccount();
  }

  get accessToken(): string | null {
    return localStorage.getItem(Models.CE.StorageKeys.accessToken);
  }

  get isTokenExpired(): boolean {
    const now = Date.now();
    const tokenExpiration = parseInt(localStorage.getItem(Models.CE.StorageKeys.accessTokenExpiration), 10);

    return now > tokenExpiration;
  }

  private setTokenData(response: any): void {
    // console.log(response);
    localStorage.setItem(Models.CE.StorageKeys.accessTokenExpiration, response.expiresOn.getTime().toString());
    localStorage.setItem(Models.CE.StorageKeys.accessToken, response.accessToken);
    localStorage.setItem('email', response.account.username);
    // console.log(response.account.username);
  }
}
